import { DatadogProvider } from '@datadog/openfeature-browser';
import {
  type Client,
  type EvaluationContext,
  OpenFeature,
  OpenFeatureProvider,
  ProviderEvents,
} from '@openfeature/react-sdk';

import constants from '../../constants.json';
import type { AccountState } from '../../reducers/account.reducer';

type CurrentAppContext = {
  AppId?: string;
  OrgId?: string;
};

type InitializeDDFeatureFlagsOptions = {
  enableExposureLogging?: boolean;
  enableFlagEvaluationTracking?: boolean;
  enableRumFeatureFlagTracking?: boolean;
};

const DATADOG_APPLICATION_ID = process.env.VITE_DATADOG_APPLICATION_ID;
const DATADOG_CLIENT_TOKEN = 'pubd9df1ad418728dbcd3a4f39e2899261c';
const client: Client = OpenFeature.getClient();
type OpenFeatureBooleanFlagKey = Parameters<Client['getBooleanValue']>[0];

let isDisabled = false;
let isReady = false;
let isInitialized = false;
let currentDDFeatureFlagsOptions: Required<InitializeDDFeatureFlagsOptions> | undefined;
// Tracks the latest init attempt so stale async completions cannot overwrite newer state.
let latestInitializeDDFeatureFlagsToken = 0;

const defaultInitializeDDFeatureFlagsOptions: Required<InitializeDDFeatureFlagsOptions> = {
  enableExposureLogging: true,
  enableFlagEvaluationTracking: true,
  enableRumFeatureFlagTracking: true,
};

function getDefaultBooleanDetails(defaultValue: boolean): {
  value: boolean;
  variant: string;
  reason: string;
} {
  return {
    value: defaultValue,
    variant: defaultValue ? 'true' : 'false',
    reason: 'DEFAULT',
  };
}

function getOpenFeatureBooleanFlagKey(key: string): OpenFeatureBooleanFlagKey {
  return key as OpenFeatureBooleanFlagKey;
}

function createDDFeatureFlagsBridge(): NonNullable<Window['fdFeatureFlags']> {
  return {
    getBooleanValue: (key, defaultValue) => {
      return isDisabled
        ? defaultValue
        : client.getBooleanValue(getOpenFeatureBooleanFlagKey(key), defaultValue);
    },
    getBooleanDetails: (key, defaultValue) => {
      return isDisabled
        ? getDefaultBooleanDetails(defaultValue)
        : client.getBooleanDetails(getOpenFeatureBooleanFlagKey(key), defaultValue);
    },
    getStringValue: (key, defaultValue) => {
      return isDisabled ? defaultValue : client.getStringValue(key, defaultValue);
    },
    getNumberValue: (key, defaultValue) => {
      return isDisabled ? defaultValue : client.getNumberValue(key, defaultValue);
    },
    onChange: (callback) => {
      const handler = (): void => {
        callback();
      };

      client.addHandler(ProviderEvents.Ready, handler);
      client.addHandler(ProviderEvents.ConfigurationChanged, handler);
      client.addHandler(ProviderEvents.ContextChanged, handler);

      return (): void => {
        client.removeHandler(ProviderEvents.Ready, handler);
        client.removeHandler(ProviderEvents.ConfigurationChanged, handler);
        client.removeHandler(ProviderEvents.ContextChanged, handler);
      };
    },
    isReady: () => !isDisabled && isReady,
  };
}

function publishDDFeatureFlagsBridge(): void {
  window.fdFeatureFlags ??= createDDFeatureFlagsBridge();
}

// Datadog feature flag environments do not support wildcards, so dev-like
// environments are intentionally collapsed into a single `dev` target.
function getDatadogEnvironment(): string {
  const resolvedEnvironment =
    constants.ENV_NAME === 'production'
      ? window.location.hostname.endsWith('.portal.flipdishdev.com')
        ? window.location.hostname.replace('.portal.flipdishdev.com', '')
        : 'production'
      : constants.ENV_NAME;

  return resolvedEnvironment === 'production' || resolvedEnvironment === 'prod-staging'
    ? resolvedEnvironment
    : 'dev';
}

function normalizeInitializeDDFeatureFlagsOptions(
  options: InitializeDDFeatureFlagsOptions = {}
): Required<InitializeDDFeatureFlagsOptions> {
  return {
    ...defaultInitializeDDFeatureFlagsOptions,
    ...options,
  };
}

function hasSameInitializeDDFeatureFlagsOptions(
  options: Required<InitializeDDFeatureFlagsOptions>
): boolean {
  return (
    currentDDFeatureFlagsOptions?.enableExposureLogging === options.enableExposureLogging &&
    currentDDFeatureFlagsOptions?.enableFlagEvaluationTracking ===
      options.enableFlagEvaluationTracking &&
    currentDDFeatureFlagsOptions?.enableRumFeatureFlagTracking ===
      options.enableRumFeatureFlagTracking
  );
}

export function initializeDDFeatureFlags(options: InitializeDDFeatureFlagsOptions = {}): void {
  publishDDFeatureFlagsBridge();

  if (!DATADOG_APPLICATION_ID || !DATADOG_CLIENT_TOKEN) {
    latestInitializeDDFeatureFlagsToken += 1;
    isReady = false;
    isDisabled = true;
    currentDDFeatureFlagsOptions = undefined;
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        "[DDFeatureFlags] Datadog feature flags are disabled because 'VITE_DATADOG_APPLICATION_ID' is missing."
      );
    }
    return;
  }

  isDisabled = false;
  const normalizedOptions = normalizeInitializeDDFeatureFlagsOptions(options);

  if (isInitialized && hasSameInitializeDDFeatureFlagsOptions(normalizedOptions)) {
    return;
  }

  currentDDFeatureFlagsOptions = normalizedOptions;
  isInitialized = true;
  isReady = false;
  const initializationToken = ++latestInitializeDDFeatureFlagsToken;

  const provider = new DatadogProvider({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    enableExposureLogging: normalizedOptions.enableExposureLogging,
    enableFlagEvaluationTracking: normalizedOptions.enableFlagEvaluationTracking,
    env: getDatadogEnvironment(),
    enableRumFeatureFlagTracking: normalizedOptions.enableRumFeatureFlagTracking,
    service: 'web-portal',
    site: 'us3.datadoghq.com',
    version: constants.VERSION,
  });

  void OpenFeature.setProviderAndWait(provider)
    .then(() => {
      if (initializationToken !== latestInitializeDDFeatureFlagsToken) {
        return;
      }
      isReady = true;
    })
    .catch((error: unknown) => {
      if (initializationToken !== latestInitializeDDFeatureFlagsToken) {
        return;
      }
      isReady = false;
      isInitialized = false;
      console.error('[DDFeatureFlags] Failed to initialize Datadog OpenFeature provider.', error);
    });
}

export function setDDFeatureFlagContext(
  account: AccountState,
  currentApp: CurrentAppContext,
  isFlipdishStaff: boolean
): void {
  if (isDisabled) {
    return;
  }

  const userId = String(account.AccountId ?? 'anonymous');
  const evaluationContext: EvaluationContext = {
    targetingKey: userId,
    userId,
    brandId: currentApp.AppId ?? '',
    isFlipdishStaff,
    orgId: currentApp.OrgId ?? '',
  };

  void OpenFeature.setContext(evaluationContext).catch((error: unknown) => {
    console.error('[DDFeatureFlags] Failed to update Datadog OpenFeature context.', error);
  });
}

export { OpenFeatureProvider };
